import { ApiError } from "@/lib/api/client";
import {
  checkCommit,
  reasonOnError,
  type BulkResult,
} from "@/lib/api/commitSeams";
import type { Candidate, Read, Result } from "@/components/ui/box.schema";

/** A page's server projection also governs a candidate after local intent. */
export function registerCandidate(
  local: Candidate,
  id: string,
  projection: { state: string; value?: { candidates: { id: string; rank: number | null; eligible: boolean; blocked: boolean; read: string }[] } } | undefined,
): Candidate {
  const server = projection?.state === "ready"
    ? projection.value?.candidates.find((row) => row.id === id)
    : undefined;
  return {
    ...local,
    // An unavailable or blocked server read cannot grant a filled act. The
    // person's quiet act can still open the work and explain its blockers.
    eligible: local.eligible && server?.eligible === true && server.blocked === false && server.read === "ready",
    rank: server?.read === "ready" ? server.rank : null,
  };
}

export const registerCopy = {
  provisional:
    "Learners see their working result as each score saves. It is provisional until an administrator certifies it.",
  advisory: "Letters are advisory: the letter scale has not been ratified.",
  scoreLimit: "Enter a score from 0 to 100, or clear the score.",
  unknown:
    "The result is not confirmed. Check what was recorded before sending anything again.",
  enrollmentSources: {
    cohort: "From a cohort",
    individual: "Individually",
    auditor: "As an auditor",
    historical_import: "From earlier records",
  },
  lineTypes: {
    assignment: "Assignment",
    quiz: "Quiz",
    midterm: "Midterm",
    final: "Final",
    participation: "Participation",
    oral_evaluation: "Oral evaluation",
  },
  outcome: {
    final_grade_not_submitted: "Final grade not submitted",
    proposal_pending_certification: "Awaiting certification",
    certified: "Certified",
    corrected: "Corrected",
  },
  leftOut: (name: string) => `${name}, auditor: no final grade`,
  weight: (sum: number) =>
    `Assessment weights total ${sum}%. They must total 100% before submission.`,
};

/** Retain a failed refresh's last read and time, without inventing empty data. */
export function registerRead<T>(
  query: {
    data?: T;
    isPending?: boolean;
    isLoading?: boolean;
    isError: boolean;
    dataUpdatedAt?: number;
    refetch: () => unknown;
  },
  label: string,
): Read<T> {
  if (query.data !== undefined) {
    const readAt = new Date(query.dataUpdatedAt || Date.now()).toISOString();
    return query.isError
      ? {
          status: "stale",
          last: query.data,
          readAt,
          sentence: `${label} could not be refreshed. A later change may be missing.`,
          refresh: () => void query.refetch(),
        }
      : { status: "ready", value: query.data, readAt };
  }
  if (query.isError)
    return {
      status: "failed",
      sentence: `${label} are unavailable.`,
      retry: () => void query.refetch(),
    };
  return { status: "loading" };
}

export function recorded(effects: string[], version?: string): Result {
  return {
    state: "confirmed",
    at: new Date().toISOString(),
    effects,
    remains: [],
    ...(version ? { version } : {}),
  };
}

/** Store recovery results too: the kit invokes Check and Retry, the area owns the result. */
export function keepResult(
  result: Result,
  store: (value: Result) => void,
): Result {
  const keep = (value: Result): Result => keepResult(value, store);
  const retry = result.state === "partial" ? result.retry : undefined;
  const held: Result =
    result.state === "unknown"
      ? {
          ...result,
          check: {
            ...result.check,
            run: async () => keep(await result.check.run()),
          },
        }
      : result.state === "partial" && retry
        ? {
            ...result,
            retry: {
              ...retry,
              run: async (key) => keep(await retry.run(key)),
            },
          }
        : result;
  store(held);
  return held;
}

export function bulkResult(
  result: BulkResult,
  key: string,
  retry: () => Promise<Result>,
): Result {
  const went = result.succeeded.map(({ id, reason }) => ({
    ids: [id],
    sentence: reason.message,
  }));
  if (result.state === "confirmed")
    return recorded(went.map((item) => item.sentence));
  return {
    state: "partial",
    at: new Date().toISOString(),
    went,
    failed: result.failed.map(({ id, reason }) => ({
      ids: [id],
      sentence: reason.message,
      fix: reason.fix?.target,
    })),
    requestId: key,
    retry: {
      verb: "Retry failed records",
      note: "Only the records that failed are sent again.",
      run: retry,
    },
  };
}

/** A lost response retains its key; Check never repeats the command. */
export async function registerCommit<T>(
  key: string,
  send: () => Promise<T>,
  confirmed: (value: T) => Promise<Result> | Result,
): Promise<Result> {
  const unknown = () => recoverRegisterCommit(key, confirmed);
  try {
    return await confirmed(await send());
  } catch (error) {
    if (error instanceof ApiError && error.status < 500)
      return {
        state: "refused",
        cap: "Not recorded",
        sentence: reasonOnError(error)?.message ?? error.message,
        draft: "kept",
      };
    return unknown();
  }
}


export const noticeCopy = {
  title: "Enrollment notice", record: "Recorded notice", send: "Send notice", retry: "Retry notice",
  check: "Check", back: "Back to roster", notSent: "Not sent", notConfirmed: "Not confirmed",
  choose: "Choose a notice from a learner's roster row.",
  unavailable: "This enrollment notice is unavailable for the selected course.",
  readVersion: "Read the latest notice before sending it.",
  checkBeforeSend: "Check the recorded attempt before sending another notice.",
  checkNote: "Check what happened to this notice without sending it again.",
  unknown: "Notice acceptance is not confirmed. Check the recorded attempt before sending anything again.",
  notRecorded: "This send was not recorded. Check the current notice before sending it.",
  accepted: "The mail service accepted this enrollment notice. Inbox delivery and reading are not proven.",
  when: "When you send this notice",
  effect: "Send only this recorded enrollment notice. Keep enrollment and academic history unchanged.",
  reason: (reason: string) => `Recorded reason: ${reason}`,
  contact: (source: "invitation" | "application" | "registrar_roster") => `Contact from the ${{ invitation: "enrollment invitation", application: "admitted application", registrar_roster: "person record" }[source]}.`,
  states: {
    pending: { label: "Ready to send", detail: "This notice is ready to send. Sending resumes this same recorded notice." },
    sending: { label: "Sending", detail: "This notice is being sent. Check its result before taking another action." },
    accepted: { label: "Accepted for sending", detail: "The mail service accepted this message for sending. Inbox delivery and reading are not proven." },
    failed: { label: "Not sent", detail: "This notice could not be sent. Check the reason before retrying this notice." },
    not_configured: { label: "Mail setup unavailable", detail: "Ask the institution to correct its sending configuration before retrying." },
    uncertain: { label: "Not confirmed", detail: "Ask the institution's sending administrator to check the previous attempt before deciding whether another message is needed." },
  },
};

/** Reconstruct Check for an interrupted command; never resends its write. */
export function recoverRegisterCommit<T>(
  key: string, confirmed: (value: T) => Promise<Result> | Result,
): Result {
  const unknown = (): Result => ({
    state: "unknown",
    cap: "Not confirmed",
    sentence: registerCopy.unknown,
    requestId: key,
    check: {
      verb: "Check",
      note: "Check what was recorded without sending it again.",
      run: async () => {
        try {
          const checked = await checkCommit<T>(key, (value) => value as T);
          if (checked.state === "confirmed" || checked.state === "partial")
            return confirmed(checked.receipt.result);
          if (checked.state === "not_committed")
            return {
              state: "refused",
              cap: "Not recorded",
              sentence:
                "Your changes were not recorded. Review them before sending again.",
              draft: "kept",
            };
          return unknown();
        } catch {
          return unknown();
        }
      },
    },
  });
  return unknown();
}

/** Shared register interface words. Record values and versions stay in their reads. */
export const registerText = {
  studentAcademicRecord: "Student · Academic record",
  sendOrCheckRecordedNotice: "Send or check one recorded enrollment notice without repeating enrollment",
  a: "A",
  absences: "absences",
  activeLearners: " active learners",
  addALine: "Add a line",
  addAnAuditor: "Add an auditor",
  addAnotherAuditor: "Add another auditor",
  addAnotherLine: "Add another line",
  addAuditor: "Add auditor",
  addingAuditorsIsUnavailableForThisTermOrView: "Adding auditors is unavailable for this term or view.",
  addingLearnersIsUnavailableInThisView: "Adding learners is unavailable in this view.",
  addLearners: "Add learners",
  addLine: "Add line",
  addMoreLearners: "Add more learners",
  advisory: " · advisory",
  aFieldSavesWhenYouLeaveItEscapeRestoresIts: "A field saves when you leave it. Escape restores its saved value.",
  alreadyEnrolled: "Already enrolled",
  aNewerRollIsAvailableYourMarksAreKeptChoose: "A newer roll is available. Your marks are kept; choose which marks to record.",
  applyEnrollment: "Apply enrollment",
  aProposalIsProvisionalUntilAnAdministratorCertifiesIt: "A proposal is provisional until an administrator certifies it.",
  aProposalIsProvisionalUntilAnAdministratorCertifiesItIt: "A proposal is provisional until an administrator certifies it. It does not enter GPA or official history before certification. Evidence: ",
  aReloadTabCloseOrLostConnectionKeepsThisTeacher: ". A reload, tab close or lost connection keeps this teacher’s draft; signing out clears it.",
  assessment: "Assessment",
  assessmentFieldSaved: "Assessment field saved",
  assessmentLineAdded: "Assessment line added",
  assessmentLineRecorded: "Assessment line recorded",
  assessmentLineRemoved: "Assessment line removed",
  assessmentLines: "Assessment lines",
  assessmentLineUpdated: "Assessment line updated",
  attendance: "% attendance",
  attendance2: "Attendance",
  attendanceAppearsAfterAnInstructorRecordsASession: "Attendance appears after an instructor records a session.",
  attendanceLog: "Attendance log",
  attendanceLogs: "Attendance logs",
  attendanceRegister: "Attendance register",
  attendanceRegisters: "Attendance registers",
  audit: "Audit",
  auditorAdded: "Auditor added",
  auditorAddedNoCreditSeatOrFinalGradeIsCreated: "Auditor added. No credit seat or final grade is created.",
  auditors: "auditors",
  auditParticipationUsesNoCreditSeatAndCreatesNoFinal: "Audit participation uses no credit seat and creates no final grade.",
  averageAttendance: "% average attendance",
  awaitingCertification: "awaiting certification",
  awaitingCertification2: "Awaiting certification",
  byIMAMCourseworkSubmitted: "by IMAM coursework. Submitted",
  cameraOff: " · camera off",
  capacity: "Capacity",
  certificationQueue: "Certification queue",
  certified: "Certified",
  certifiedStanding: "Certified standing",
  certifiedTerms: "certified terms",
  certify: "Certify",
  certifyExactProposal: "Certify exact proposal",
  change: "Change",
  changedNotRecorded: "Changed, not recorded",
  changeLine: "Change line",
  checked: "Checked",
  check: "Check",
  checkingCloseout: "Checking closeout",
  checkingCourseCloseoutStatus: "Checking course closeout status.",
  checkingCurrentWorkingResultAvailability: "Checking current working result availability.",
  checkingPlan: "Checking plan...",
  checkingYourProvisionalResult: "Checking your provisional result.",
  checkNotices: "Check notices",
  chooseAnAvailableSession: "Choose an available session.",
  chooseAnExistingPersonExplicitlyNewExternalPeopleRemainAccountless: "Choose an existing person explicitly. New external people remain accountless until the institution approves linking.",
  chooseAPersonOrCohortToAdd: "Choose a person or cohort to add.",
  chooseHowToUseTheNewerAssessmentBasis: "Choose how to use the newer assessment basis.",
  chooseHowToUseTheNewerAssessmentLine: "Choose how to use the newer assessment line.",
  chooseHowToUseTheNewerGradebook: "Choose how to use the newer gradebook.",
  chooseHowToUseTheNewerOutcomeBeforeRecordingAn: "Choose how to use the newer outcome before recording an event.",
  chooseHowToUseTheNewerRosterBeforeAddingThis: "Choose how to use the newer roster before adding this auditor.",
  choosePeople: "Choose people",
  choosePeopleCheckThePlanThenApplyItOnce: "Choose people, check the plan, then apply it once.",
  closeoutRecordsCouldNotBeRefreshedEachCourseKeepsIts: "Closeout records could not be refreshed. Each course keeps its last known certification and names current working availability.",
  closeoutStatusIsUnavailableNoResultIsShownAsOfficial: "Closeout status is unavailable. No result is shown as official.",
  closeoutUnavailable: "Closeout unavailable",
  cohorts: "Cohorts",
  cohortsAreUnavailable: "Cohorts are unavailable.",
  complete: "Complete",
  contactSupportAboutThisResult: "Contact support about this result",
  contribution: "Contribution",
  correct: "Correct",
  corrected: "Corrected",
  correctedLetterGrade: "Corrected letter grade",
  correctedNumericScoreOptional: "Corrected numeric score (optional)",
  correction: "Correction ",
  correctionRecordedPriorCertificationPreserved: "Correction recorded. Prior certification preserved.",
  correctStatus: "Correct status",
  course: "Course %",
  courseCloseout: "Course closeout",
  courseCloseoutStatus: "Course closeout status",
  courseOutcomes: "Course outcomes",
  courseOutcomesAreUnavailableNoResultIsShownAsOfficial: "Course outcomes are unavailable. No result is shown as official.",
  courseOutcomesHaveNotBeenRead: "Course outcomes have not been read.",
  courseResults: "Course results",
  courseResultsAreUnavailable: "Course results are unavailable.",
  courses: "courses",
  createNoNewInitialEnrollmentNotices: "Create no new initial enrollment notices",
  credit: "Credit",
  credit2: "credit",
  credits: " credits · ",
  creditSeats: "Credit seats: ",
  creditSeats2: "Credit seats",
  currentCertification: "Current certification",
  currentLearners: "Current learners",
  currentLetter: "Current letter",
  currentName: "Current name",
  currentParticipation: "Current participation",
  currentProposal: "Current proposal",
  currentScore: "Current score",
  currentStatus: "Current status",
  currentTotal: "Current total",
  currentWeight: "Current weight",
  deferInitialEnrollmentNoticesUntilTheCourseIsPublished: "Defer initial enrollment notices until the course is published",
  downstreamImpact: "Downstream impact",
  e: "E",
  eachLineCarriesAShareOfTheCourseResult: "Each line carries a share of the course result.",
  eachTermKeepsItsCertificationState: "Each term keeps its certification state.",
  earlierDraftToReEnter: "Earlier draft to re-enter: ",
  emailOptional: "Email (optional)",
  enrolledLearners: "Enrolled learners",
  enrollmentAndAcademicHistoryArePreservedNoticeAcceptanceDoesNot: "Enrollment and academic history are preserved. Notice acceptance does not prove inbox delivery.",
  enrollmentIsAlreadyRecordedOnlyTheFailedNoticesAreRetried: "Enrollment is already recorded. Only the failed notices are retried.",
  enrollmentIsRecordedCheckTheExistingNoticesBeforeSendingAgain: "Enrollment is recorded. Check the existing notices before sending again.",
  enrollmentRecordedNoticesNotConfirmed: "Enrollment recorded; notices not confirmed",
  enrollmentStatus: "Enrollment status",
  enrollmentUpdatedOriginalProvenanceAndAcademicRecordsPreserved: "Enrollment updated. Earlier enrollment details and grades are kept.",
  enterAScoreFrom0To100ALetterAnd: "Enter a score from 0 to 100, a letter, and the reason after reading the latest result.",
  enterAtLeast2CharactersANameOrEmailNever: "Enter at least 2 characters. A name or email never links a person automatically.",
  enterAWholeNumberInThisFieldSRange: "Enter a whole number in this field's range.",
  evidenceReference: "Evidence reference",
  evidence: "Evidence",
  required: "Required",
  submittedValue: "Submitted value",
  source: "Source",
  imamGradebook: "IMAM gradebook",
  lockTheAssessmentLinesAndEveryScore: "Lock the assessment lines and every score",
  exactProposalCertified: "Exact proposal certified.",
  excused: "excused",
  existingPeopleCouldNotBeRead: "Existing people could not be read.",
  finalGradeProposals: "Final grade proposals",
  finalGradesProposedForAdministratorCertification: "Final grades proposed for administrator certification.",
  finalProposals: "Final proposals",
  finalProposalsEnterAcademicHistoryOnlyAfterAnAdministratorCertifies: "Final proposals enter academic history only after an administrator certifies them.",
  finalResultsWereSubmittedForAdministratorReviewButAreNot: "Final results were submitted for administrator review but are not yet certified.",
  finalScore: "Final score",
  fromCoursework: " · from coursework",
  giveThePersonSLegalName: "Give the person's legal name.",
  gradeHistory: "Grade history",
  grades: "Grades",
  heldSessions: "held sessions",
  hideRoll: "Hide roll",
  incomplete: "Incomplete",
  l: "L",
  lastRecordedWithdrawal: "Last recorded withdrawal: ",
  learner: "Learner",
  learners: "learners",
  learnersAdded: "Learners added",
  legalName: "Legal name",
  letter: "Letter",
  letterGrade: "Letter grade",
  lineAdded: "Line added",
  lines: "lines",
  loadNewerScore: "Load newer score",
  manageEnrollment: "Manage enrollment",
  markALearnerAndMoveToTheNext: " mark a learner and move to the next.",
  markAllPresent: "Mark all present",
  markAtLeastOneLearnerBeforeRecordingTheRoll: "Mark at least one learner before recording the roll.",
  marks: "Marks",
  marksAreKeptInThisBrowser: "Marks are kept in this browser ",
  marksCouldNotBeKeptInThisBrowserKeepThis: "Marks could not be kept in this browser. Keep this page open until the roll is recorded.",
  matchingPeople: "Matching people",
  message: "Message ",
  name: "Name",
  nameTheAssessmentAndItsShareOfTheWorkingResult: "Name the assessment and its share of the working result.",
  nameTheAssessmentLine: "Name the assessment line.",
  newAssessmentLine: "New assessment line",
  newerScore: "Newer score: ",
  newExternalPerson: "New external person",
  noAssessmentLinesYet: "No assessment lines yet.",
  noAttendanceRecordsYet: "No attendance records yet",
  noAttendanceRecordsYetAttendanceAppearsAfterAnInstructorRecords: "No attendance records yet. Attendance appears after an instructor records a session.",
  noCourseRecordsYet: "No course records yet",
  noCurrentWorkingResultIsAvailableForThisCourseCertified: "No current working result is available for this course. Certified history and corrections remain visible above.",
  noEligibleRosterChangesAreInThisPlanReviewEach: "No eligible roster changes are in this plan. Review each person's recorded reason.",
  noEnrolledLearners: "No enrolled learners.",
  noLearnersAreEnrolled: "No learners are enrolled.",
  noLearnersInThisAttendanceRegister: "No learners in this attendance register.",
  noLearnersOnThisRoster: "No learners on this roster.",
  noLimit: " · no limit",
  noMark: "No mark",
  noMarkHere: "No mark here",
  noMatchingPeople: "No matching people.",
  noPercentageLetterTotalOrItemContributionIsShown: ". No percentage, letter, total, or item contribution is shown.",
  noProposalsAwaitingCertification: "No proposals awaiting certification",
  noSessionAttendanceHasBeenRecordedForThisCourse: "No session attendance has been recorded for this course.",
  noSessionHasBeenHeldYet: "No session has been held yet.",
  notAdded: "Not added",
  notAvailable: "Not available",
  notChecked: "Not checked",
  notConfirmed: "Not confirmed",
  noTermsOnFile: "No terms on file",
  notMarked: "Not marked",
  notRead: "Not read",
  notRecorded: "Not recorded",
  notSubmitted: "Not submitted",
  notYetAvailable: "Not yet available",
  off: "off",
  on: "on",
  onlyCertifiedOutcomesContributeToGPA: "Only certified outcomes contribute to GPA.",
  onlyNeededIfThePlanExceedsTheCreditSeatLimit: "Only needed if the plan exceeds the credit-seat limit. Audit participation uses no credit seat.",
  openEnrollmentNotice: "Open enrollment notice",
  originalEnrollmentProvenanceAndAcademicHistoryStayRecorded: "Keep earlier enrollment details and grades.",
  originalSource: "Added:",
  notCurrentlyActive: "Not currently active",
  outcomeCloseout: "Outcome closeout",
  overall: "% overall",
  override: "Override",
  overrideReason: "Override reason",
  p: "P",
  participation: "Participation",
  pending: "pending",
  peopleCouldNotBeRead: "People could not be read.",
  person: "Person",
  personChoice: "Person choice",
  planChecked: "Plan checked",
  planPreview: "Plan preview",
  position: "Position",
  previewPlan: "Preview plan",
  previewThisExactPlanBeforeApplyingIt: "Preview this exact plan before applying it.",
  proposed: "Proposed ",
  proposesFinalGradesForAdministratorReview: "Proposes final grades for administrator review.",
  provisional: "Provisional",
  provisionalCurrent: "Provisional · current",
  provisionalTerms: "provisional terms",
  readALearnerSMarksInTheirRow: "Read a learner's marks in their row.",
  readAnAvailableSessionSRollBeforeMarkingIt: "Read an available session's roll before marking it.",
  readCoursesAgain: "Read courses again",
  readEnrollment: "Read enrollment",
  readingCohorts: "Reading cohorts...",
  readingMatchingPeople: "Reading matching people...",
  readingRoll: "Reading roll",
  readingTheSessionRoll: "Reading the session roll...",
  readMarks: "Read marks",
  readOutcome: "Read outcome",
  readResult: "Read result",
  readScoringSource: "Read scoring source",
  readsTheOriginalBatchAndItsDeliveryRecords: "Check which learners were enrolled and which notices were sent.",
  readTerm: "Read term",
  readTheCurrentRosterBeforeAddingAnAuditor: "Read the current roster before adding an auditor.",
  readTheCurrentRosterBeforeApplyingAPlan: "Read the current roster before applying a plan.",
  readTheLatestAssessmentLinesBeforeChangingThem: "Read the latest assessment lines before changing them.",
  readTheLatestAssessmentLinesBeforeSaving: "Read the latest assessment lines before saving.",
  readTheLatestOutcomeBeforeRecordingAnEvent: "Read the latest outcome before recording an event.",
  readTheLatestScoresBeforeSaving: "Read the latest scores before saving.",
  readTheRollFirst: "Read the roll first.",
  readTheSelectedSessionSRollBeforeMarkingIt: "Read the selected session's roll before marking it.",
  readThisRollAgain: "Read this roll again",
  readyToSubmit: "Ready to submit",
  reason: "Reason",
  reasonForExceedingCapacity: "Reason for exceeding capacity",
  recordCorrection: "Record correction",
  recordedAttendance: "Recorded attendance",
  recordedEnrollments: "Recorded enrollments",
  recordedScoresReferToThisLineKeepItAndSet: "Recorded scores refer to this line. Keep it and set its weight to 0% instead.",
  recording: "Recording",
  recordingWithheld: "Recording withheld",
  recordOverride: "Record override",
  recordRoll: "Record roll",
  reinstate: " · Reinstate",
  reinstate2: "Reinstate",
  removeLine: "Remove line",
  removeThisLine: "Remove this line",
  removeThisLineFromTheWorkingResult: "Remove this line from the working result",
  resolveTheNewerRollBeforeRecordingTheseMarks: "Resolve the newer roll before recording these marks.",
  resultsAreStillProvisionalAndAreNotPartOfOfficial: "Results are still provisional and are not part of official GPA or history.",
  retryCloseoutStatus: "Retry closeout status",
  retryCohorts: "Retry cohorts",
  retryFailedNotices: "Retry failed notices",
  enrollmentRecordedStartAnotherPlan: "Enrollment is recorded. You can start another enrollment plan now. Failed notices also stay on each learner's roster row.",
  retryPeople: "Retry people",
  retrySearch: "Retry search",
  retryWorkingResult: "Retry working result",
  returnToApplicantFile: "Return to applicant file",
  returnToCourses: "Return to courses",
  reviewReinstatementInAddLearners: "Review reinstatement in Add learners",
  roll: "Roll",
  rollChanges: "Roll changes",
  rollNotRecorded: "Roll not recorded",
  rollRecorded: "Roll recorded",
  rollRecordedForThisSession: "Roll recorded for this session.",
  rollUnavailable: "Roll unavailable",
  roster: "Roster",
  rosters: "Rosters",
  savedValue: "Saved value",
  saveLine: "Save line",
  saveParticipation: "Save participation",
  scheduled: "scheduled",
  scores: "Scores",
  scoreSaved: "Score saved",
  scoresByAssessment: "Scores by assessment",
  searchNameOrSID: "Search name or SID",
  selectASession: "Select a session",
  selectedSession: "Selected session",
  session: "Session",
  sessionRolls: "Session rolls",
  sessionUnavailable: "Session unavailable",
  showRoll: "Show roll",
  standing: "Standing",
  statusCorrection: "Status correction",
  submitFinalGrades: "Submit final grades",
  submitRoll: "Submit roll",
  submitted: "Submitted",
  submittedForCertification: "Submitted for certification",
  submitting: "Submitting…",
  takeRoll: "Take roll",
  termsOnFile: "Terms on file",
  thatCourseIsNotOnYourRecord: "That course is not on your record. ",
  theCourseRegisterIsUnavailable: "The course register is unavailable.",
  theRecordedMarkForEachLearnerAndHeldSession: "The recorded mark for each learner and held session.",
  theRegisterForEachCourseAndTheRollForA: "The register for each course, and the roll for a single session. A closed term stays readable but takes no new marks.",
  theRequestedCourseIsNotInYourAttendanceRecord: "The requested course is not in your attendance record.",
  theRosterIsUnavailable: "The roster is unavailable.",
  theSessionRollCouldNotBeRead: "The session roll could not be read.",
  thisAssessmentLineIsUnavailable: "This assessment line is unavailable.",
  thisCourseRegisterIsUnavailable: "This course register is unavailable.",
  thisEnrollmentChangedChooseWhichDraftToKeep: "This enrollment changed. Choose which draft to keep.",
  thisPlanExceedsTheCreditSeatLimitGiveAReason: "This plan exceeds the credit-seat limit. Give a reason before applying it.",
  thisProvisionalResultIsUnavailableNoPercentageLetterTotalOr: "This provisional result is unavailable. No percentage, letter, total, or item contribution is shown.",
  thisRosterIsUnavailable: "This roster is unavailable.",
  thisSessionIsCancelledItsRollCannotBeRecorded: "This session is cancelled. Its roll cannot be recorded.",
  thisSessionIsUnavailableInThisCourseChooseAnAvailable: "This session is unavailable in this course. Choose an available session.",
  thisTermIsClosedItsAttendanceRegisterRemainsReadable: "This term is closed. Its attendance register remains readable.",
  thisTermIsClosedItsRosterRemainsReadableAddingAnd: "This term is closed. Its roster remains readable; adding and reinstating are unavailable.",
  thisTermIsClosedWithdrawalAndStatusCorrectionsRemainAvailable: "This term is closed. Withdrawal and status corrections remain available.",
  to: " to",
  type: "Type",
  unchanged: ". Unchanged.",
  untilYouCloseTheSession: "until you close the session",
  untilYouRecordTheRoll: "until you record the roll",
  updateRoll: "Update roll",
  useMyScoreOnTheNewerVersion: "Use my score on the newer version",
  viewAllCourses: "View all courses",
  viewYourAttendance: "View your attendance",
  watchRecordingOpensInANewTab: "Watch recording (opens in a new tab)",
  weight: "Weight %",
  weight2: "Weight",
  weightMustBeAWholeNumberFrom0To100: "Weight must be a whole number from 0 to 100.",
  whenYouAddTheLine: "When you add the line",
  whenYouAddThisAuditor: "When you add this auditor",
  whenYouApplyThisPlan: "When you apply this plan",
  whenYouCertify: "When you certify",
  whenYouChangeThisLine: "When you change this line",
  whenYouOverrideThisResult: "When you override this result",
  whenYouRecordTheCorrection: "When you record the correction",
  whenYouRecordTheRoll: "When you record the roll",
  whenYouSubmitFinalGrades: "When you submit final grades",
  whoToAdd: "Who to add",
  withdraw: "Withdraw",
  withdrawalReason: "Withdrawal reason",
  workingFinalResultOverriddenCertificationStaysSeparate: "Working final result overridden. Certification stays separate.",
  workingResultAvailabilityCouldNotBeLoadedYourCourseCloseout: "Working result availability could not be loaded. Your course closeout record remains available.",
  workingResultOnlyFinalGPAAndAcademicHistoryUseA: "Working result only. Final GPA and academic history use a separate certification or correction event.",
  workingResultsPerCourseAndCertifiedTermHistory: "Working results per course and certified term history.",
  workingResultsRemainProvisionalUntilExplicitlyCertified: "Working results remain provisional until explicitly certified.",
  workingScores: "Working scores",
  yourAcademicRecord: "Your academic record",
  yourDraft: "Your draft",
  yourParticipation: "Your participation",
  yourScore: ". Your score:",
  yourScore2: "Your score",
  yourSessionBySessionAttendanceRecord: "Your session-by-session attendance record.",
  yourSessionBySessionRecordForEveryCourseWithThe: "Your session-by-session record for every course, with the recording made available where a session was excused.",
  yourStatusChoice: "Your status choice",
  loadTheNewerBasis: "Load the newer basis",
  keepMyNewLineOnTheNewerBasis: "Keep my new line on the newer basis",
  theAssessmentLinesChangedYourNewLineIsKept: "The assessment lines changed. Your new line is kept.",
  assessmentLines2: "assessment lines",
  addOneAssessmentLine: "Add one assessment line",
  loadTheNewerLine: "Load the newer line",
  keepMyDraftOnTheNewerLine: "Keep my draft on the newer line",
  thisLineChangedYourDraftIsKept: "This line changed. Your draft is kept.",
  assessmentLine: "assessment line",
  defineTheBasisOfTheWorkingResult: "Define the basis of the working result",
  loadTheNewerValue: "Load the newer value",
  keepMyValueOnTheNewerLine: "Keep my value on the newer line",
  thisAssessmentFieldChangedYourDraftIsKept: "This assessment field changed. Your draft is kept.",
  assessmentField: "assessment field",
  proposeFinalResultsForExplicitAdministratorCertification: "Propose final results for explicit administrator certification",
  loadTheNewerGradebook: "Load the newer gradebook",
  keepMyOverrideOnTheNewerGradebook: "Keep my override on the newer gradebook",
  theGradebookChangedYourOverrideDraftIsKept: "The gradebook changed. Your override draft is kept.",
  workingFinalResult: "working final result",
  auditor: "Auditor",
  readAndRecordProvisionalAssessmentScores: "Read and record provisional assessment scores",
  loadTheNewerOutcome: "Load the newer outcome",
  keepMyDraftOnTheNewerOutcome: "Keep my draft on the newer outcome",
  thisOutcomeChangedYourEvidenceAndCorrectionDraftAreKept: "This outcome changed. Your evidence and correction draft are kept.",
  courseOutcome: "course outcome",
  keepProposalsSeparateFromCertifiedAcademicOutcomes: "Keep proposals separate from certified academic outcomes",
  keepingYourEditsRecordsYourChangedMarksAndKeepsTheirs: " Keeping your edits records your changed marks and keeps theirs for every other learner.",
  thisRoll: "this roll",
  recordThisSessionSRoll: "Record this session's roll",
  chooseAndReadTheRollBeforeMarking: "Choose and read the roll before marking",
  readALearnerSAttendanceAndOpenTheirThread: "Read a learner's attendance and open their thread",
  loadTheNewerRoster: "Load the newer roster",
  keepMyAuditorOnTheNewerRoster: "Keep my auditor on the newer roster",
  theRosterChangedYourAuditorDetailsAreKept: "The roster changed. Your auditor details are kept.",
  roster2: "roster",
  enrollADeliberatelySelectedStaffOrExternalAuditor: "Enroll a deliberately selected staff or external auditor",
  loadTheNewerEnrollment: "Load the newer enrollment",
  keepMyDraftOnTheNewerEnrollment: "Keep my draft on the newer enrollment",
  thisEnrollmentChangedAfterYouBeganThisDraft: "This enrollment changed after you began this draft.",
  enrollment: "enrollment",
  readAndCorrectRecordedEnrollments: "Read and correct recorded enrollments",
  chooseAndReviewAnExactEnrollmentPlan: "Choose and review an exact enrollment plan",
  readYourAttendanceRecord: "Read your attendance record",
  cameraOff2: " · Camera off",
  readYourDatedAttendanceMarks: "Read your dated attendance marks",
  readEachLearnerSMarksAgainstHeldSessions: "Read each learner's marks against held sessions",
  yourAssessmentBreakdown: "Your assessment breakdown",
  readCertifiedAndProvisionalTermHistory: "Read certified and provisional term history",
  readYourCourseResults: "Read your course results",
  noCertifiedGPAYet: "No certified GPA yet",
  readYourCertifiedStanding: "Read your certified standing",
  weightNeeded: "weight needed",
  newAssessment: "New assessment",
  unnamedLine: "Unnamed line",
  readTheLatestGradebookBeforeSubmitting: "Read the latest gradebook before submitting.",
  everyCreditLearnerNeedsAMappableFinalResultBeforeSubmission: "Every credit learner needs a mappable final result before submission.",
  noOpenCreditEnrollmentsToSubmit: "No open credit enrollments to submit.",
  saveOrRestoreTheEditedAssessmentFieldsAndScoresBefore: "Save or restore the edited assessment fields and scores before submitting.",
  aLetter: "a letter",
  noScoreYet: "No score yet",
  aSubmittedOrCertifiedResultStandsOnTheseAssessmentLines: "A submitted or certified result stands on these assessment lines. Record a correction instead.",
  theCorrectedLetter: "the corrected letter",
  notCertified: "Not certified",
  noProposal: "No proposal",
  correctedNumericScoreMustBeFrom0To100: "Corrected numeric score must be from 0 to 100.",
  declareTheDownstreamImpact: "Declare the downstream impact.",
  reasonIsRequired: "Reason is required.",
  correctedLetterIsRequired: "Corrected letter is required.",
  evidenceReferenceIsRequired: "Add an evidence reference.",
  someoneSavedANewerVersionOfThisRollYourMarks: "Someone saved a newer version of this roll. Your marks are kept here.",
  couldNotSubmitTheRoll: "Could not submit the roll",
  couldNotMarkTheRoll: "Could not mark the roll",
  allPresentMarksRecordedForThisSession: "All present marks recorded for this session.",
  thisSessionHasEndedSubmittingTheRollMarksItHeld: "This session has ended. Submitting the roll marks it held.",
  thisSessionIsCancelledSoItsRollCanTBe: "This session is cancelled, so its roll can't be taken.",
  changesUpdateItsRecordedRoll: "Changes update its recorded roll.",
  thisSessionIsScheduledSubmittingTheRollMarksItHeld: "This session is scheduled. Submitting the roll marks it held.",
  thisOlderDraftHasNoStartingRollToCompareLoad: "This older draft has no starting roll to compare. Load their version, then re-enter your edits from the marks shown here.",
  reloadTheRollBeforeTryingAgain: "Reload the roll before trying again.",
  markAllPresentAndMarksAreMutuallyExclusive: "mark_all_present and marks are mutually exclusive",
  noLearnersAreEnrolledOnThisCourseThereIsNo: "No learners are enrolled on this course. There is no roll to record.",
  noStudentsToMarkEmptyRoster: "No students to mark (empty roster)",
  provideEitherMarkAllPresentTrueOrANonEmpty: "Provide either mark_all_present=true or a non-empty marks list",
  thisSessionIsCancelledSoItsRollCanTBe2: "This session is cancelled, so its roll can't be recorded.",
  thisSessionIsCancelledAttendanceCannotBeMarked: "This session is cancelled; attendance cannot be marked",
  marksForLearnersNoLongerOnThisCourseSRoster: "Marks for learners no longer on this course's roster have been removed from your draft.",
  theseStudentIdSAreNotOnThisCourseS: "These student_id(s) are not on this course's roster: ",
  recordTheMarkedLearnersForThisSession: "Record the marked learners for this session",
  readTheMarksBeforeRecordingTheRoll: "Read the marks before recording the roll.",
  thisTermIsClosedTheRosterStaysReadableAndTakes: "This term is closed. The roster stays readable and takes no new marks.",
  thisLearnerIsNotOnTheRoster: "This learner is not on the roster.",
  thisPerson: "this person",
  reasonRequired: "reason required",
  theCreditSeatLimitIsFullGiveACapacityReason: "The credit-seat limit is full. Give a capacity reason.",
  giveAWithdrawalReason: "Give a withdrawal reason.",
  thisEnrollmentIsAlreadyWithdrawn: "This enrollment is already withdrawn.",
  reviewReinstatementBeforeChangingParticipation: "Review reinstatement before changing participation.",
  thisTermIsClosedParticipationStaysRecorded: "This term is closed. Participation stays recorded.",
  readTheLatestEnrollmentBeforeChangingIt: "Read the latest enrollment before changing it.",
  enrollmentChangesAreUnavailableInThisView: "Enrollment changes are unavailable in this view.",
  cohortNameUnavailable: "Cohort name unavailable",
  explicitReinstatementRequired: "Explicit reinstatement required",
  noCohort: "No cohort",
  thePlanCouldNotBeCheckedYourChoicesAreKept: "The plan could not be checked. Your choices are kept.",
  thisTerm: "This term",
  thisProvisionalResultIsUnavailable: "This provisional result is unavailable",
  thisProvisionalResultCannotBeShownSafely: "This provisional result cannot be shown safely",
  thisProvisionalResultIsNoLongerCurrent: "This provisional result is no longer current",
  workingResult: "Working result",
} as const;

export const registerPhrases = {
  attendance: { present: "Present", absent: "Absent", excused: "Excused", late: "Late" },
  totalWeight: (value: number) => `Total ${value}%`,
  lineEffect: (name: string, weight: string) => `${name}: ${weight || "weight needed"}% of the working result`,
  overrideEffect: (letter: string) => `Record ${letter || "a letter"} as the working final result; certification stays separate`,
  proposalEffect: (name: string, letter: string) => `${name}: propose ${letter}`,
  correctionEffect: (letter: string) => `Record ${letter || "the corrected letter"} in a new event and preserve the previous certification`,
  certifyEffect: (letter: string) => `Certify the exact submitted proposal: ${letter}`,
  eventLabel: (kind: string, number: number) => `${kind === "correction" ? "Correction" : "Certification"} event ${number}`,
  prior: (letter: string) => `, prior ${letter}`,
  reason: (reason: string) => `. Reason: ${reason}`,
  impact: (impact: string) => `. Impact: ${impact}`,
  evidence: (reference: string) => `. Evidence: ${reference}`,
  provisionalLetter: (letter: string) => `${letter} · provisional`,
  score: (value: number, preview = false) => `${value}%${preview ? " preview" : ""}`,
  cumulativeGpa: (value: number) => `Cumulative GPA ${value.toFixed(2)}`,
  workingResult: (code: string) => `${code} working result`,
  relatedWork: (line: string) => `Coursework for ${line}`,
  points: (value: number) => `${value.toFixed(1)} pts`,
  letterTo: (letter: string) => `${letter} to `,
  closedAttendance: (term: string) => `${term} is closed. No learners in this attendance register.`,
  cameraOff: (date: string) => `${date} · Camera off`,
  attendanceMark: (status: string, cameraOff: boolean) => `${status}${cameraOff ? " · camera off" : ""}`,
  followUpAbsences: (count: number) => `${count} absences to follow up`,
  participation: (kind: string, present: number, held: number) => `${kind}${held ? ` · ${present} of ${held} present` : ""}`,
  marksFor: (name: string) => `${name}'s marks`,
  attendanceFor: (name: string) => `${name} attendance`,
  cameraFor: (name: string) => `Camera on for ${name}`,
  markFor: (name: string) => `Mark for ${name}`,
  savedMark: (status: string, camera: boolean) => `${status}, camera ${camera ? "on" : "off"}`,
  removedMarks: (list: string) => `No longer on this course's roster: ${list}. Their marks have been removed from your draft.`,
  nameList: (names: string[]) => names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0],
  rollRecorded: (marked: number, total: number) => `Roll recorded · ${marked} of ${total} learners marked`,
  rollCounts: (marked: number, unmarked: number, state: string) => `${marked} marked · ${unmarked} unmarked · ${state}`,
  unsentMarks: (count: number) => `${count} mark${count === 1 ? "" : "s"} not submitted`,
  recordedCount: (recorded: number, total: number) => `${recorded} of ${total} recorded`,
  changedMark: (name: string, before: string, after: string, camera: boolean | null) => `${name}: ${before} to ${after}${camera === null ? "" : `; camera ${camera ? "on" : "off"}`}`,
  namedMark: (name: string, status: string, camera: boolean | null) => `${name}: ${status}${camera === null ? "" : `, camera ${camera ? "on" : "off"}`}`,
  added: (name: string) => `${name} added`,
  reinstated: (name: string) => `${name} reinstated`,
  capacity: (count: number) => ` of ${count}`,
  reinstateEffect: (name: string, sid: string) => `Reinstate ${name} · ${sid}; preserve their academic records`,
  noticeEffect: (count: number) => `Send ${count} enrollment notice${count === 1 ? "" : "s"} after learners are enrolled`,
  enrollmentChange: (name: string) => `When you change ${name}'s enrollment`,
  withdrawEffect: (name: string, reason: string) => `Withdraw ${name} with reason: ${reason || "reason required"}; preserve their academic records`,
  enrollmentEffect: (before: string, after: string) => `Change ${before} to ${after} on the same enrollment`,
  auditorEffect: (name: string) => `Add ${name || "this person"} as an auditor; preserve existing roles and academic history`,
};
